import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import type {
  CoachingApprovalFields,
  CoachingApplicationDraft,
  CoachingVideoReference,
} from "@/lib/types";
import {
  isCurrentDraftContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
export const feedbackFields = [
  "title",
  "body",
  "player_summary",
  "what_happened",
  "why_it_matters",
  "what_to_do_next",
] as const;
export const actionFields = [
  "title",
  "description",
  "success_criteria",
] as const;
export type HumanEdits = Partial<CoachingApprovalFields>;
export type ReviewOrigin = {
  captureId: string;
  draftId: string;
  draftRevision: number;
  confirmationId: string;
  confirmationRevision: number;
};
export type ReviewCarry = {
  kind: "feedback" | "action";
  origin: ReviewOrigin;
  edits: HumanEdits;
  video?: CoachingVideoReference;
};
export function proposedWriting(
  draft: CoachingApplicationDraft,
): CoachingApprovalFields {
  const empty = {
    title: "",
    body: "",
    player_summary: "",
    what_happened: "",
    why_it_matters: "",
    what_to_do_next: "",
    description: "",
    success_criteria: "",
  };
  return draft.kind === "feedback"
    ? {
        ...empty,
        title: draft.proposed_fields.title,
        body: draft.proposed_fields.body,
        player_summary: draft.proposed_fields.summary,
      }
    : {
        ...empty,
        title: draft.proposed_fields.title,
        description: draft.proposed_fields.description,
        success_criteria: draft.proposed_fields.success_criteria,
      };
}
/** Only fields independently touched by the human, never an untouched proposal. */
export function boundedHumanEdits(
  dirty: HumanEdits,
  kind: "feedback" | "action",
): HumanEdits {
  const allowed = kind === "feedback" ? feedbackFields : actionFields;
  const result: HumanEdits = {};
  for (const key of allowed) {
    const value = dirty[key];
    if (typeof value === "string")
      result[key] = value.slice(
        0,
        key === "title" ? 160 : key === "body" ? 4000 : 2000,
      );
  }
  return result;
}
const carryOwners = new Map<string, DraftContext>();
function removeDepartedCarries() {
  for (const [storageKey, owner] of carryOwners) {
    if (!isCurrentDraftContext(owner)) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* unavailable */
      }
      carryOwners.delete(storageKey);
    }
  }
}
useAuthStore.subscribe(removeDepartedCarries);
useScopeStore.subscribe(removeDepartedCarries);
function key(owner: DraftContext) {
  return `tktic_cr_draft:inbox:${JSON.stringify([owner.actorId, owner.clubId, owner.teamId, owner.seasonId])}`;
}
export function saveReviewCarry(owner: DraftContext, carry: ReviewCarry): void {
  if (!isCurrentDraftContext(owner)) return;
  carryOwners.set(key(owner), owner);
  try {
    sessionStorage.setItem(
      key(owner),
      JSON.stringify({
        kind: carry.kind,
        origin: {
          captureId: carry.origin.captureId,
          draftId: carry.origin.draftId,
          draftRevision: carry.origin.draftRevision,
          confirmationId: carry.origin.confirmationId,
          confirmationRevision: carry.origin.confirmationRevision,
        },
        edits: boundedHumanEdits(carry.edits, carry.kind),
        ...(validVideo(carry.video)
          ? {
              video: {
                match_id: carry.video.match_id,
                slot: carry.video.slot,
                timestamp_seconds: carry.video.timestamp_seconds,
                match_slug: carry.video.match_slug,
                reference_revision: carry.video.reference_revision,
              },
            }
          : {}),
      }),
    );
  } catch {
    /* unavailable storage */
  }
}
export function clearReviewCarry(owner: DraftContext): void {
  try {
    sessionStorage.removeItem(key(owner));
  } catch {
    /* unavailable */
  }
}
export function clearReviewCarryForOrigin(
  owner: DraftContext,
  origin: { captureId: string; draftId: string },
): void {
  const carry = readReviewCarry(owner);
  if (
    carry?.origin.captureId === origin.captureId &&
    carry.origin.draftId === origin.draftId
  )
    clearReviewCarry(owner);
}
export function readReviewCarry(owner: DraftContext): ReviewCarry | null {
  if (!isCurrentDraftContext(owner)) return null;
  try {
    const carry: unknown = JSON.parse(
      sessionStorage.getItem(key(owner)) ?? "null",
    );
    if (
      !carry ||
      typeof carry !== "object" ||
      !("origin" in carry) ||
      !("edits" in carry)
    )
      return null;
    const value = carry as ReviewCarry;
    const o = value.origin;
    if (
      !o ||
      ![o.captureId, o.draftId, o.confirmationId].every(
        (id) => typeof id === "string" && id.length > 0 && id.length <= 120,
      ) ||
      !Number.isInteger(o.draftRevision) ||
      o.draftRevision < 1 ||
      !Number.isInteger(o.confirmationRevision) ||
      o.confirmationRevision < 1
    )
      return null;
    carryOwners.set(key(owner), owner);
    return {
      kind: value.kind === "action" ? "action" : "feedback",
      origin: {
        captureId: o.captureId,
        draftId: o.draftId,
        draftRevision: o.draftRevision,
        confirmationId: o.confirmationId,
        confirmationRevision: o.confirmationRevision,
      },
      edits: boundedHumanEdits(
        value.edits,
        value.kind === "action" ? "action" : "feedback",
      ),
      ...(validVideo(value.video) ? { video: value.video } : {}),
    };
  } catch {
    return null;
  }
}
/** Unknown acknowledgement freezes the operation body. New edits require explicit reset. */
export class FrozenOperation<T> {
  private request: T | null = null;
  begin(make: () => T): T {
    this.request ??= structuredClone(make());
    return structuredClone(this.request);
  }
  reset(): void {
    this.request = null;
  }
  get pending(): boolean {
    return this.request !== null;
  }
}
export function recipientsReviewable(
  recipients: Array<{
    user_id: string;
    display_name: string | null;
    label_status: string;
  }>,
  ids: string[],
): boolean {
  return (
    recipients.length === ids.length &&
    recipients.every(
      (r, index) =>
        r.user_id === ids[index] &&
        r.label_status === "available" &&
        !!r.display_name?.trim(),
    )
  );
}

function validVideo(value: unknown): value is CoachingVideoReference {
  if (!value || typeof value !== "object") return false;
  const v = value as CoachingVideoReference;
  return (
    typeof v.match_id === "string" &&
    v.match_id.length <= 120 &&
    typeof v.match_slug === "string" &&
    v.match_slug.length <= 200 &&
    [
      "full",
      "first_half",
      "second_half",
      "extra_time",
      "penalty_shootout",
    ].includes(v.slot) &&
    Number.isFinite(v.timestamp_seconds) &&
    v.timestamp_seconds >= 0 &&
    typeof v.reference_revision === "string" &&
    /^[a-f0-9]{64}$/.test(v.reference_revision)
  );
}
